import { Link } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, MessageSquare, Star } from "lucide-react";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useSolution } from "@/hooks/use-solutions";
import { useSetCurrentVersion, useVersions } from "@/hooks/use-versions";

export function VersionHistoryPage({ slug }: { slug: string }) {
  const sol = useSolution(slug);
  const versions = useVersions(slug);
  const setCurrent = useSetCurrentVersion(slug);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/solutions/$slug" params={{ slug }}>
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="min-w-0">
              <div className="text-base truncate">{sol.data?.name ?? "Solution"}</div>
              <div className="text-[11px] text-muted-foreground">Version history</div>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-3">
        {versions.isLoading && <Skeleton className="h-32" />}
        {versions.isError && !versions.isLoading && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-center">
            <p className="text-sm text-destructive mb-3">Could not load version history.</p>
            <Button variant="outline" size="sm" onClick={() => versions.refetch()}>
              Try again
            </Button>
          </div>
        )}
        {!versions.isLoading && !versions.isError && versions.data?.length === 0 && (
          <div className="rounded-lg border-2 border-dashed border-border bg-muted/30 p-12 text-center">
            <p className="text-sm text-muted-foreground">
              No versions yet. Upload an architecture file from the solution page.
            </p>
          </div>
        )}
        {versions.data?.map((v) => (
          <Card key={v.id} className={v.isCurrent ? "border-primary/40 shadow-sm" : ""}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="text-2xl w-16 shrink-0 text-center">v{v.versionNumber}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {v.label && <span className="font-medium text-sm">{v.label}</span>}
                  {v.isCurrent && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      current
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
                  <span>{new Date(v.createdAt).toLocaleString()}</span>
                  <span>by {v.uploadedByName}</span>
                  {v.commentCount > 0 && (
                    <span className="flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" />
                      {v.commentCount} comment{v.commentCount === 1 ? "" : "s"}
                      {v.unresolvedCount > 0 && (
                        <span className="text-amber-700 dark:text-amber-400 ml-1">
                          ({v.unresolvedCount} open)
                        </span>
                      )}
                    </span>
                  )}
                </div>
                {v.changeSummary && (
                  <p className="text-xs mt-1.5 text-muted-foreground border-l-2 border-border pl-2">
                    {v.changeSummary}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" asChild>
                  <Link to="/solutions/$slug/v/$versionId" params={{ slug, versionId: v.id }}>
                    View
                  </Link>
                </Button>
                {!v.isCurrent && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrent.mutate(v.id)}
                    disabled={setCurrent.isPending}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                    Set current
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </main>
    </div>
  );
}
